import axios from 'axios';
/* eslint-disable import/no-cycle */
// eslint-disable-next-line import/no-cycle
import { adminApi } from '@/includes/contentManagementApi';
import i18n from '@/includes/i18n';

export default class NetworkContent {
  static getTeams(config) {
    return adminApi.get('teams', config);
  }

  static async getShortUsers(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/user/getShortUsers?scope=teamspace
    return adminApi.get('user/getShortUsers', config);
  }

  /**
   * Get assessment overview for the current user (home task list).
   * Migrated to Monorepo: GET /content-management-api/assessments/user/overview
   * Each assessment includes displayIntroPage:
   * true when intro should be shown before the assessment.
   */
  static async getAssessmentsOverview(config) {
    return adminApi.get('assessments/user/overview', config);
  }

  static async getParticipants(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/user/kssParticipants
    return adminApi.get('user/kssParticipants', config);
  }

  static async getArchivedTasks(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/assessments/kss/archived
    return adminApi.get('assessments/kss/archived', config);
  }

  static async getCreatedTasks(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/assessments/kss/created
    return adminApi.get('assessments/kss/created', config);
  }

  static async createKssTask(data, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/assessments/kss/create
    return adminApi.post('assessments/kss/create', data, config);
  }

  static async createExternalEvaluation(data, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/assessments/externalEvaluation/create
    return adminApi.post('assessments/externalEvaluation/create', data, config);
  }

  static async createAdminExternalEvaluation(data, config) {
    // Admin-only: POST /content-management-api/assessments/externalEvaluation/admin/create
    return adminApi.post('assessments/externalEvaluation/admin/create', data, config);
  }

  static getAdminExternalEvaluations(config) {
    // Admin-only: GET /content-management-api/assessments/externalEvaluation/admin
    return adminApi.get('assessments/externalEvaluation/admin', config);
  }

  static getAdminKssSeries(config) {
    // Admin-only: GET /content-management-api/assessments/kss/admin
    return adminApi.get('assessments/kss/admin', config);
  }

  static async deleteKssTask(data, config) {
    // Migrated to Monorepo endpoint
    // DELETE /content-management-api/assessments/kss/:seriesId
    // seriesId is the primary identifier for KSS series in Monorepo
    const seriesId = data.seriesId || data._id;
    return adminApi.delete(`assessments/kss/${seriesId}`, config);
  }

  static async endKssSeries(seriesId, config) {
    // POST /content-management-api/assessments/kss/:seriesId/end
    return adminApi.post(`assessments/kss/${seriesId}/end`, {}, config);
  }

  static async reactivateKssSeries(seriesId, data, config) {
    // POST /content-management-api/assessments/kss/:seriesId/reactivate (admin only)
    return adminApi.post(`assessments/kss/${seriesId}/reactivate`, data, config);
  }

  static async updateKssTask(seriesId, data, config) {
    // Migrated to Monorepo endpoint
    // PUT /content-management-api/assessments/kss/:seriesId
    return adminApi.put(`assessments/kss/${seriesId}`, data, config);
  }

  static async updateTaskEventsStatus(data, config) {
    // Migrated to Monorepo endpoint
    // PATCH /content-management-api/assessments/kss/:assessmentId/event-status
    // data.assessmentId - the assessment ID representing the occurrence
    // data.status - 'removed_from_series' or 'scheduled'
    const { assessmentId, status } = data;
    return adminApi.patch(`assessments/kss/${assessmentId}/event-status`, { status }, config);
  }

  /** Same data as {@link getTeams}. */
  static getLeadedTeams(config) {
    return adminApi.get('teams', config);
  }

  static getAllUsers(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/user/getShortUsers?scope=all
    return adminApi.get('user/getShortUsers?scope=all', config);
  }

  static getPersonalMindset(config) {
    return axios.get('content/dashboard/personalMindset', config);
  }

  static getPersonalMindsetPlot(data, config) {
    return axios.post('content/dashboard/personalMindsetPlot', data, config);
  }

  static getNotifications(config) {
    return adminApi.get('user/notification/allPopups', config);
  }

  static deactivatePopUp(id, config) {
    return adminApi.post('user/notification/deactivatePopup', { id }, config);
  }

  static subscribeToPush(data, config) {
    return adminApi.post('user/notification/subscribe', data, config);
  }

  static getDashboardProfileDesc(config) {
    return axios.get('content/dashboard/profileDescription', config);
  }

  static async getPersonalKssPlot(config) {
    return axios.get('content/dashboard/personalKSSPlot', config);
  }

  static getPersonalMoodomatPlot(config) {
    return axios.get('content/dashboard/personalMoodomatPlot', config);
  }

  static getTeamDashboard(config) {
    return adminApi.get('content/teamDashboard/dashboardData', config);
  }

  static getDashboardHtml(config) {
    return axios.get('content/dashboard/underDiagramHTML', config);
  }

  static getTeamDashboardPlots(data, config) {
    return adminApi.post('content/teamDashboard/dashboardDataPlot', data, config);
  }

  static getInteractiveContent(data, config) {
    return axios.post('content/interactiveContent', data, config);
  }

  static getOverlayNotes(config) {
    return adminApi.get('user/notification/allOverlays', config);
  }

  static deactivateOverlay(id, config) {
    return adminApi.post('user/notification/deactivateOverlay', { id }, config);
  }

  static setTeams(data, config) {
    return adminApi.put('users/me/teams', data, config);
  }

  static setTags(data, config) {
    return axios.post('content/tags/setTags', data, config);
  }

  static getKnowledgeDatabase(config) {
    return axios.get('content/knowledgeDatabase', config);
  }

  static getKssHistory(config, dateParams = null) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/assessments/kss/history
    let url = 'assessments/kss/history';

    // Add date parameters to query string if provided
    if (dateParams && dateParams.start && dateParams.end) {
      const params = new URLSearchParams({
        start: dateParams.start.toISOString(),
        end: dateParams.end.toISOString(),
      });
      url += `?${params.toString()}`;
    }

    return adminApi.get(url, config);
  }

  static getQuickAccessLinks(config) {
    return axios.get('content/quickAccessLinks', config);
  }

  static getProjectNames(config) {
    return axios.get('content/projectNames', config);
  }

  static async getPersonalJourney(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/content/personalJourney
    return adminApi.get('content/personalJourney', config);
  }

  static getPersonalGoals(config) {
    return axios.get('assessment/getPersonalGoals', config);
  }

  static changeGoalStatus(data, config) {
    return axios.post('assessment/changeGoalStatus', data, config);
  }

  static submitPersonalGoalFeedback(data, config) {
    return axios.post('assessment/submitPersonalGoalFeedback', data, config);
  }

  static async createPersonalGoal(data, config) {
    return axios.post('assessment/personalgoals/create', data, config);
  }

  static async updatePersonalGoal(data, config) {
    return axios.post('assessment/updatePersonalGoal', data, config);
  }

  static async getTravelBuddyMeetingInfo(config) {
    return adminApi.get('assessments/travelBuddy/meetingInfo', config);
  }

  static async submitTravelBuddyFeedback(data, config) {
    return adminApi.post('assessments/travelBuddy/feedback', data, config);
  }

  static async getNewUserDemoContent(config) {
    try {
      // Migrated to new content-management backend
      const response = await adminApi.get('content/user-demo', config);
      return response;
    } catch (error) {
      console.error('Error fetching new user demo content:', error);
      return { data: { content: [] } };
    }
  }

  static async getExternalEvaluationText(config) {
    try {
      const response = await adminApi.get('content/external-evaluation-text', config);
      return response?.data?.content ?? { en: '', de: '' };
    } catch (error) {
      console.error('Error fetching external evaluation text:', error);
      return { en: '', de: '' };
    }
  }

  static async newUserDemoComplete(config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/user/newUserDemoComplete
    return adminApi.post('user/newUserDemoComplete', config);
  }

  static getExternalEvaluation(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/assessments/externalEvaluation
    return adminApi.get('assessments/externalEvaluation', config);
  }

  static async sendExternalEvaluationReminder(seriesId, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/assessments/externalEvaluation/:seriesId/reminder
    return adminApi.post(`assessments/externalEvaluation/${seriesId}/reminder`, {}, config);
  }

  static extendExternalEvaluation(seriesId, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/assessments/externalEvaluation/:seriesId/extend
    return adminApi.post(`assessments/externalEvaluation/${seriesId}/extend`, {}, config);
  }

  static updateExternalEvaluation(seriesId, data, config) {
    // Monorepo endpoint
    // PATCH /content-management-api/assessments/externalEvaluation/:seriesId
    return adminApi.patch(`assessments/externalEvaluation/${seriesId}`, data, config);
  }

  static async getKssEvalutionPerMeeting(config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/assessments/kss/dashboard/meetings
    return adminApi.get('assessments/kss/dashboard/meetings', config);
  }

  static async getKssEvalutionPerMeetingPlotData(data, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/assessments/kss/dashboard/plot
    // Handles both new format { seriesId, language } and old Vuex format { assesment: {...} }
    let transformedData;

    if (data.seriesId) {
      // New format from MyResultsDashboardComponent - pass through
      transformedData = data;
    } else if (data.assesment) {
      // Old format from Vuex store / DashboardComponent - transform
      transformedData = {
        seriesId: data.assesment.seriesId || data.assesment.task,
        language: i18n.global.locale.value || 'en',
      };
    } else {
      // Fallback
      transformedData = data;
    }

    return adminApi.post('assessments/kss/dashboard/plot', transformedData, config);
  }

  static async generateKssTestData(data, config) {
    return adminApi.post('assessments/kss/generate-test-data', data, config);
  }

  static async removeKssTestData(config) {
    return adminApi.delete('assessments/kss/test-data', config);
  }

  static sendUserFeedback(data, config) {
    return axios.post('userFeedback/sendUserFeedback', data, config);
  }

  static getReflexionGuidelineAssessments(config) {
    return adminApi.get('assessments/reflexionGuideline', config);
  }

  static async showNewUserDemo(config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/user/showNewUserDemo
    return adminApi.post('user/showNewUserDemo', config);
  }

  static async getTravelBuddyHistory(config) {
    return adminApi.get('assessments/travelBuddy/history', config);
  }

  static async getSelectedTravelBuddyMeetingInfo(data, config) {
    return adminApi.post('assessments/travelBuddy/selectedMeeting', data, config);
  }
}
